// React & Router
import React from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";

// MUI Components
import { IconButton, Stack } from "@mui/material";
import { West as WestIcon } from "components/icon/mui_icon";

// Custom & Ant Design Components
import { PlusOutlined } from "@ant-design/icons";
import { StyledButton } from "customStyled";
import OverflowTooltip from "./OverflowTooltip";
import { IconBackground, SelectIcon } from "components/icon";
import formatEnumText from "utils/formatEnumText";
import StatusDropdownUI from "components/table/UI/StatusDropdownUI";

/**
 * PageTitle Component
 * -------------------
 * Renders a page header with optional back button, title, icon, and add button.
 * Can also render additional children (e.g., filters, toggles) on the right side.
 *
 * Props:
 * - title (string): Page title text
 * - icon (string | null): Optional icon to display beside title
 * - back (boolean): Optional prop to force show back button
 * - children (ReactNode): Additional elements to render in the header
 * - addButtonClick (function | null): Callback for Add button
 * - addButtonText (string): Text for Add button
 */
const PageTitle = (props) => {
  const {
    title,
    subTitle = null,
    icon = null,
    statusValue = null,
    children,
    addButtonClick = null,
    addButtonText = "",
    back = false,
    isSticky = false,
    sx = {},
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get route params to determine if back button should be displayed
  let { campusId, buildingId, adId, tagId, domainId } = useParams();
  const showBack = !!campusId || !!buildingId || !!adId || !!tagId || !!domainId; 

  return (
    <Stack
      direction={"row"}
      spacing={0.2}
      sx={(theme) => ({
        alignItems: "center",
        justifyContent: "space-between",
        paddingRight: 3,
        // above the page content (e.g. a table's sticky header)
        zIndex: 2,
        ...(isSticky
          ? {
              position: "sticky",
              top: 0,
              background: "#ffffff",
              borderBottom: `1px solid ${theme.palette.common.border}`,
            }
          : {}),
        ...sx,
      })}
    >
      {/* Left section: Back button, Title, and Icon */}
      <Stack
        direction={"row"}
        sx={{
          gap: 1.5,
          flexGrow: 1,
          alignItems: "center",
          px: 3
        }}>
        {/* Conditional Back Button */}
        {showBack || back ? (
          <IconButton
            variant={"outline"}
            sx={(theme) => ({
              border: "1px solid #DBDDE0",
              borderRadius: theme.shape.borderRadius + "px",
            })}
            onClick={() => navigate("../")}
            aria-label={t("goBack")}
          >
            <WestIcon />
          </IconButton>
        ) : null}
        <Stack
          direction={"column"}
          sx={{
            flexGrow: 1,
            padding: "20px 24px 21px 0"
          }}>
          <Stack
            direction={"row"}
            sx={{
              gap: 1,
              flexGrow: 1,
              alignItems: "center"
            }}>
            {/* Page Title */}
            <OverflowTooltip variant="h4">{title}</OverflowTooltip>
            {/* Optional icon displayed next to title */}
            {statusValue && (
              <StatusDropdownUI
                isDisabled={true}
                value={statusValue}
                colorValue={statusValue}
                statusValue={(value) => formatEnumText(value)}
              />
            )}
            {/* Optional icon displayed next to title */}
            {icon && (
              <IconBackground>
                <SelectIcon value={icon} />
              </IconBackground>
            )}
          </Stack>
          {!!subTitle ? (
            <OverflowTooltip
              variant="h6"
              sx={{
                color: "rgba(71, 84, 103, 1)",
              }}
            >
              {subTitle}
            </OverflowTooltip>
          ) : null}
        </Stack>
        {/* <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
        </Stack> */}
      </Stack>

      {/* Right section: Add button and any children elements */}
      <Stack direction={"row"} spacing={1} sx={{
        alignItems: "center"
      }}>
        {/* Conditional Add Button */}
        {addButtonClick ? (
          <StyledButton
            // sx={{ height: 44 }}
            variant="contained"
            startIcon={<PlusOutlined />}
            onClick={addButtonClick}
            sx={{marginTop: 1}}
          >
            {addButtonText}
          </StyledButton>
        ) : null}

        {/* Additional elements passed as children */}
        {children}
      </Stack>
    </Stack>
  );
};

// Export with memo to prevent unnecessary re-renders
export default React.memo(PageTitle);
